import CampaignIcon from '@mui/icons-material/Campaign'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import DashboardIcon from '@mui/icons-material/Dashboard'
import DescriptionIcon from '@mui/icons-material/Description'
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import QueueMusicIcon from '@mui/icons-material/QueueMusic'
import { isCompanyCountryFrance } from '@/lib/country/entityCountries'
import { RADIO_DISTRIBUTOR } from '@/lib/documents/documentEnums'
import { routeFrontOfficeCommonUser } from '@/lib/routes/front/common/routeFrontOfficeCommonUser'
import { routeFrontOfficeInvoices } from '@/lib/routes/front/common/routeFrontOfficeInvoices'
import { routeFrontOfficeMyCampaigns } from '@/lib/routes/front/common/routeFrontOfficeMyCampaigns'
import { isRadioSubscribed } from '@/lib/sound/radioSubscription'
import { TypeCardDashboard } from '@/types/Dashboard/DashboardType'
import { EntityList } from '@/types/Entity'
import { CONSTANTS } from '@/types/enum/constants'
import { QUERY_SOUND_SUBSCRIPTION } from '@/types/sound/constantSound'

const soundPharmacienAll = [
  {
    route: 'Dashboard',
    text: 'Tableau de bord',
    t: 'routes.frontoffice.sound.dashboard',
    icon: DashboardIcon,
    href: '/frontoffice/sound/home',
    home: true,
    menuHeader: true,
    position: 10,
    // Pas de badge ici : le compteur est porte par le groupe « Actions a faire » du menu
    // (projection de /me/actions), sur une ligne qui NOMME l'action. Une pastille sur
    // « Tableau de bord » annoncait un travail sans dire lequel, et restait affichee alors
    // que la page ouverte ne proposait rien a faire.
  },
].map(route => ({
  ...route,
  // Sound ACTIF uniquement : un module non active n'affiche pas de tableau de bord vide —
  // son entree de menu EST l'action d'activation (soundPharmacienOnboarding ci-dessous).
  roles: [
    CONSTANTS.ROLE_DYNAMIC_ROUTE_SOUND_PHARMACIEN_ACTIVE,
    CONSTANTS.ROLE_DYNAMIC_ROUTE_SOUND_PHARMACIEN_IT,
    CONSTANTS.ROLE_DYNAMIC_ROUTE_SOUND_MANAGER_RADIO,
  ],
}))

// Sound NON ACTIVE : « Activer ma radio » remplace « Tableau de bord ». La cible est la
// home Sound AVEC le tunnel d'onboarding ouvert (?step — CardOnboardingSound ouvre sa
// dialog plein ecran, stepper horizontal conserve), sur le modele de l'activation Italie.
// ATTENTION : les roles ONBOARDING_FR/IT sont ADDITIFS (tout titulaire FR/IT les porte,
// meme Sound actif). D'ou le fnDisplay : sans lui, une pharmacie active voyait
// « Tableau de bord » ET « Activer ma radio ».
//
// 20/09/2026 : le test porte desormais sur l'ABONNEMENT (`isRadioSubscribed` : `subscribed`
// ou `off_platform_subscription`) et non sur `pharmacieSound.enabled`. Le bouton est le
// parcours des NON abonnes — `subscribed` veut dire que l'onboarding a ete fait,
// `off_platform` qu'un admin l'a pose depuis le BO. Le flag legacy laissait sans aucun
// chemin les fiches marquees actives mais jamais abonnees : ni bouton (masque par le flag),
// ni popup d'onboarding (reservee aux abonnes depuis le 01/09/2026).
const soundPharmacienOnboarding = [
  {
    route: 'SoundActivateRadio',
    text: 'Activer ma radio',
    t: 'routes.frontoffice.sound.activate',
    icon: DashboardIcon,
    href: '/frontoffice/sound/home',
    queryParams: { step: QUERY_SOUND_SUBSCRIPTION, type: TypeCardDashboard.RESUMES },
    home: true,
    menuHeader: true,
    position: 10,
    fnDisplay: ({ dataMyProfile }) => !isRadioSubscribed(dataMyProfile?.selectCompanyDefault),
  },
].map(route => ({
  ...route,
  roles: [
    CONSTANTS.ROLE_DYNAMIC_ROUTE_SOUND_ONBOARDING_PHARMACIEN_FR,
    CONSTANTS.ROLE_DYNAMIC_ROUTE_SOUND_ONBOARDING_PHARMACIEN_IT,
  ],
}))

const soundManagerRadio = [
  {
    route: 'audiotheque',
    text: 'Audiothèque',
    t: 'routes.frontoffice.sound.audiotheque',
    icon: LibraryMusicIcon,
    href: '/frontoffice/sound/audiotheque',
    menuHeader: true,
    position: 20,
    fnDisplay: ({ dataMyProfile }) =>
      dataMyProfile?.selectCompanyDefault?.pharmacieSound?.distributor ==
      RADIO_DISTRIBUTOR.RADIOSHOP,
  },
  {
    route: 'myPlaylist',
    text: 'myPlaylist',
    mobileText: 'Playlist',
    t: 'routes.frontoffice.sound.myMusic',
    icon: QueueMusicIcon,
    href: '/frontoffice/sound/my-music',
    menuHeader: true,
    // Sous-menu (menu v2) : rendu indente sous l'audiotheque ; si le parent est
    // invisible (roles), l'entree remonte automatiquement au niveau 1.
    parentRoute: 'audiotheque',
    position: 21,
  },
  {
    route: 'SoundAvailableSpots',
    text: 'Spots disponibles',
    t: 'routes.frontoffice.sound.availableSpots',
    icon: PlaylistAddIcon,
    href: '/frontoffice/sound/available-spots',
    menuHeader: true,
    parentRoute: 'audiotheque',
    position: 22,
    // Même condition que la page (SoundPublicSpotOptInService::radioBlocker) : seule une radio
    // Saooti active a un planning, en France uniquement. Espace Pharmedigroup : la page parle de
    // la radio de la pharmacie.
    fnDisplay: ({ dataMyProfile }) => {
      const company = dataMyProfile?.selectCompanyDefault

      return (
        company?.pharmacieSound?.distributor === RADIO_DISTRIBUTOR.SAOOTI &&
        isRadioSubscribed(company) &&
        isCompanyCountryFrance(company)
      )
    },
  },
  {
    route: 'audiotheque-edit',
    text: 'Editer une piste audio',
    t: 'routes.frontoffice.sound.audiotheque-edit',
    href: '/frontoffice/sound/audiotheque/edit/[spotId]',
  },
  {
    route: 'GuidesDocumentations',
    text: 'Guides & Documentations',
    t: 'routes.frontoffice.sound.documentation',
    icon: MenuBookIcon,
    href: '/frontoffice/group/documentation',
    menuHeader: true,
    position: 210,
  },
  {
    route: 'SoundUlysseInstall',
    href: '/frontoffice/sound/home',
    queryParams: { installPlayer: true },
  },
  {
    route: 'SoundOnboarding',
    href: '/frontoffice/sound/home',
    queryParams: { step: QUERY_SOUND_SUBSCRIPTION, type: TypeCardDashboard.RESUMES },
  },
].map(route => ({
  ...route,
  roles: [
    CONSTANTS.ROLE_DYNAMIC_ROUTE_SOUND_PHARMACIEN_ACTIVE,
    CONSTANTS.ROLE_DYNAMIC_ROUTE_SOUND_PHARMACIEN_IT,
    CONSTANTS.ROLE_DYNAMIC_ROUTE_SOUND_MANAGER_RADIO,
  ],
}))

const soundPharmacienActive = [
  {
    // HORS menu : fusionnee dans « Mes campagnes » transverse (Ma pharmacie). La page
    // reste servie pour les deep-links existants (et l'audiotheque de campagne).
    route: 'campaigns',
    text: 'Mes campagnes',
    mobileText: 'Campagnes',
    t: 'routes.frontoffice.sound.campaigns',
    icon: CampaignIcon,
    href: '/frontoffice/sound/campaigns',
    position: 20,
  },
  ...routeFrontOfficeMyCampaigns,
  {
    route: 'Contrat',
    text: 'Contrat',
    t: 'routes.frontoffice.sound.contracts',
    icon: DescriptionIcon,
    href: '/frontoffice/group/contracts',
    menuHeader: true,
    position: 40,
  },
  ...routeFrontOfficeCommonUser,
].map(route => ({
  ...route,
  roles: [
    CONSTANTS.ROLE_DYNAMIC_ROUTE_SOUND_PHARMACIEN_ACTIVE,
    CONSTANTS.ROLE_DYNAMIC_ROUTE_SOUND_PHARMACIEN_IT,
  ],
}))

const soundPharmacienInvoice = [
  {
    route: 'myMysteryVisits',
    text: 'myMysteryVisits',
    t: 'routes.frontoffice.sound.myMysteryVisits',
    href: '/frontoffice/sound/diffusion-control',
    menuHeader: false,
    position: 31,
  },
  ...routeFrontOfficeInvoices,
  {
    route: 'Sales',
    text: 'Abonnements',
    t: 'routes.frontoffice.all.sales',
    icon: CreditCardIcon,
    href: '/frontoffice/group/billing/sales-invoices',
    menuHeader: true,
    position: 110,
    isMobile: false,
  },
].map(route => ({
  ...route,
  roles: [CONSTANTS.ROLE_DYNAMIC_ROUTE_SOUND_PHARMACIEN_ACTIVE],
}))

export const getFrontOfficeSoundPharmacien = [
  ...soundPharmacienAll,
  ...soundPharmacienOnboarding,
  ...soundPharmacienActive,
  ...soundManagerRadio,
  ...soundPharmacienInvoice,
].map(route => ({
  ...route,
  entity: [EntityList.PHARMEDISOUND],
}))
